/**
 * Mirror tool overlay (⇧M / toolbar Mirror): captures the two axis clicks the way the dimension and
 * calibration tools do (Excalidraw mustn't select or move anything), draws the axis and a dashed ghost of
 * the mirrored selection, and shows the step banner with the "Keep original" toggle.
 */
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import { useEffect, useRef, useState } from 'react';
import { snapAxis } from '../../shared/src/measureTools';
import { axisFrom, mirrorSelection, outlineOf, type MEl, type Pt } from './mirror';

export interface MirrorState {
  ids: string[];
  pts: Pt[];
}

const KEEP_KEY = 'xb.mirror.keep';
export function readMirrorKeep(): boolean {
  try {
    return localStorage.getItem(KEEP_KEY) !== '0';
  } catch {
    return true;
  }
}
export function writeMirrorKeep(v: boolean): void {
  try {
    localStorage.setItem(KEEP_KEY, v ? '1' : '0');
  } catch {
    /* private mode */
  }
}

export function mirrorBanner(clicks: number): string {
  return clicks === 0
    ? 'Mirror · click the start of the axis · Alt-click: vertical axis there · Esc cancels'
    : 'Mirror · click the end of the axis · Shift: level / plumb · Esc cancels';
}

export function MirrorOverlay(p: {
  xapi: ExcalidrawImperativeAPI;
  tick: number;
  mirror: MirrorState;
  keep: boolean;
  onKeep(v: boolean): void;
  onPoint(pt: Pt, alt: boolean): void;
  onCancel(): void;
}) {
  const { xapi, mirror } = p;
  void p.tick; // re-render on camera / scene changes
  const st = xapi.getAppState();
  const z = st.zoom.value;
  const vp = (q: Pt): Pt => [(q[0] + st.scrollX) * z, (q[1] + st.scrollY) * z];
  const ref = useRef(p);
  ref.current = p;
  const [hover, setHover] = useState<Pt | null>(null);

  useEffect(() => {
    const host = document.querySelector('.xb-canvas .excalidraw');
    if (!host) return;
    const toScene = (e: PointerEvent): Pt => {
      const a = xapi.getAppState();
      const r = (host as HTMLElement).getBoundingClientRect();
      const zz = a.zoom.value;
      const pt: Pt = [(e.clientX - r.left) / zz - a.scrollX, (e.clientY - r.top) / zz - a.scrollY];
      const m = ref.current.mirror;
      if (m.pts.length !== 1 || !e.shiftKey) return pt;
      const q = snapAxis(m.pts[0], pt);
      return [q[0], q[1]];
    };
    const down = (ev: Event) => {
      const e = ev as PointerEvent;
      const t = e.target as HTMLElement | null;
      if (!t || t.tagName !== 'CANVAS' || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      ref.current.onPoint(toScene(e), e.altKey);
    };
    const move = (ev: Event) => setHover(toScene(ev as PointerEvent));
    const key = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        ref.current.onCancel();
      }
    };
    host.addEventListener('pointerdown', down, true);
    host.addEventListener('pointermove', move, true);
    window.addEventListener('keydown', key, true);
    return () => {
      host.removeEventListener('pointerdown', down, true);
      host.removeEventListener('pointermove', move, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [xapi]);

  // Axis being defined: first click → hover. Ghost: the selection mirrored across it.
  const a = mirror.pts.length === 1 && hover ? axisFrom(mirror.pts[0], hover) : null;
  let ghost: { pts: Pt[]; closed: boolean }[] = [];
  let axisLine: [Pt, Pt] | null = null;
  if (a) {
    const scene = xapi.getSceneElementsIncludingDeleted() as unknown as MEl[];
    ghost = mirrorSelection(scene, mirror.ids, a, { duplicate: true, now: 0, newId: () => 'ghost' })
      .added.filter((e) => e.type !== 'text' || !e.containerId)
      .map((e) => outlineOf(e));
    // Long enough to cross the viewport at any zoom.
    const d: Pt = [a.q[0] - a.p[0], a.q[1] - a.p[1]];
    const len = Math.hypot(d[0], d[1]) || 1;
    const far = (st.width + st.height) / z;
    axisLine = [
      [a.p[0] - (d[0] / len) * far, a.p[1] - (d[1] / len) * far],
      [a.p[0] + (d[0] / len) * far, a.p[1] + (d[1] / len) * far],
    ];
  }
  return (
    <div className="xb-measure" data-testid="mirror-overlay">
      <div className="xb-cal-hint xb-mirror-hint" data-testid="mirror-banner" data-step={mirror.pts.length}>
        <label className="xb-mirror-keep" title="On: mirrored copies (originals stay). Off: flip the selection in place.">
          <input type="checkbox" checked={p.keep} onChange={(e) => p.onKeep(e.target.checked)} data-testid="mirror-keep" /> Keep original
        </label>
        <span>{mirrorBanner(mirror.pts.length)}</span>
      </div>
      <svg className="xb-cal-svg" width="100%" height="100%">
        {mirror.pts.map((q, i) => {
          const [x, y] = vp(q);
          return <circle key={i} cx={x} cy={y} r={4} />;
        })}
        {axisLine ? <line className="xb-mirror-axis" x1={vp(axisLine[0])[0]} y1={vp(axisLine[0])[1]} x2={vp(axisLine[1])[0]} y2={vp(axisLine[1])[1]} data-testid="mirror-axis" /> : null}
        {ghost.map((g, i) => {
          const pts = g.pts.map((q) => vp(q).join(',')).join(' ');
          return g.closed ? <polygon key={i} className="xb-mirror-ghost" points={pts} /> : <polyline key={i} className="xb-mirror-ghost" points={pts} />;
        })}
      </svg>
    </div>
  );
}
